import {
  type IOfferPayloadEnterprise,
  type IPostApplyAndOffer,
  useGetOfferUrl,
  usePatchApplyV2,
  usePostApply,
  usePostApplyAndOffer,
  usePostDataOffer,
  usePostOfferPinned,
  usePutDataOffer,
  useSendFeedbackToData,
} from "@api";
import type { IApply, IApplyPatch, IOfferPostApply, IPostApplyOffer } from "@bbj-types/apply";
import type { IContact } from "@bbj-types/contact";
import type { IJob } from "@bbj-types/job";
import type { IOffer } from "@bbj-types/offer";
import type { ObjectId } from "@bbj-types/utils";
import { useBatchCreateActions } from "@root/api/services/action/batchCreateActions";
import { useGlobalContext } from "@root/context";
import { toStartOfDayISO } from "@root/utils/date/toStartOfDayISO";
import { isInternalOfferSource } from "@root/utils/enums/offer";
import { useI18nContext } from "@root/utils/organisation";
import { addDays, isDate, isValid } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { IEnterpriseBody, TModalOfferMode } from "../context";
import {
  formatContact,
  formatCreateOffer,
  formatEnterprise,
  formatPutOffer,
  generateJobFollowUpAction,
  getApplyContractEndDate,
  isOnConfirmApply,
  isOnConfirmOffer,
  verifyOfferFields,
} from "./logics";

export interface IApplyCreate {
  status: string;
  isFollowUpGenerated: boolean;
  contractStartDate?: string;
  itwDate?: Date;
}

export const initApply: IApplyCreate = {
  status: "",
  isFollowUpGenerated: false,
};

export const useModalOffer = <IsApply extends boolean | undefined = undefined>(
  open: boolean,
  detailOffer: Partial<IOffer> | undefined,
  onConfirm: (result: IsApply extends true ? IApply : IOffer) => void,
  onCancel: () => void,
  isApply?: IsApply,
  defaultApplyValues?: IsApply extends true ? Partial<IApplyCreate> : never,
  candidateId?: ObjectId,
  managementProcess?: IsApply extends true ? ObjectId : undefined,
  initEnterprise?: IEnterpriseBody,
  applyFollowUp = false,
  mode: TModalOfferMode = "create",
  applyId?: ObjectId
) => {
  const { user } = useGlobalContext();
  const { timeZone } = useI18nContext();
  const [{ loading: loadingPost }, postOffer] = usePostDataOffer();
  const [{ loading: loadingPut }, putOffer] = usePutDataOffer();
  const [{ loading: loadingPutPinned }, putOfferPinned] = usePostOfferPinned();
  const [{ loading: loadingPostApplyAndOffer }, postApplyAndOffer] = usePostApplyAndOffer();
  const [{ loading: loadingPostApply }, postApply] = usePostApply();
  const [{ loading: loadingPatchApply }, patchApply] = usePatchApplyV2();
  const [{ data: offerUrl, loading: loadingUrl }, getOfferUrl, cancelGetOfferUrl] = useGetOfferUrl();
  const [{ loading: loadingCreateAction }, createAction] = useBatchCreateActions();

  const [, sendFeedback] = useSendFeedbackToData();

  const [offer, setOffer] = useState<Partial<IOffer>>({});
  const [apply, setApply] = useState<IApplyCreate>({ ...initApply, ...defaultApplyValues });
  const [followUpDate, setFollowUpDate] = useState<Date | null>(null);
  const initialIsFollowUpGenerated = useRef<boolean>(defaultApplyValues?.isFollowUpGenerated ?? false);
  const initialFollowUpDate = useRef<number | null>(null);
  const initialCreateOffer = useRef<boolean>(false);
  const [createOffer, setCreateOffer] = useState<boolean>(false);

  const [enterprise, setEnterprise] = useState<IEnterpriseBody | null>(initEnterprise ?? null);
  const [contact, setContact] = useState<IContact | null>(null);
  const [appellation, setAppellation] = useState<IJob | null>(null);
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [searchUrlDone, setSearchUrlDone] = useState<boolean>(false);
  const [isSharedEnterprise, setIsSharedEnterprise] = useState<boolean>(false);

  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setApply({ ...initApply, ...defaultApplyValues });
      initialIsFollowUpGenerated.current = defaultApplyValues?.isFollowUpGenerated ?? false;
      initialFollowUpDate.current = null;
      setFollowUpDate(null);
      setSearchUrlDone(false);
      initialCreateOffer.current = !!detailOffer?.offerId;
      setCreateOffer(!!detailOffer?.offerId);
      setIsSharedEnterprise(false);
      setErrors({
        externalSource: false,
        address: false,
        name: false,
        appellation: false,
        enterprise: false,
        status: false,
        itwDate: false,
      });
      if (detailOffer) {
        setOffer({
          ...detailOffer,
        });
        if (detailOffer.enterprise) {
          setEnterprise(detailOffer.enterprise);
        }
        setContact(detailOffer.contact ?? null);
      } else {
        if (initEnterprise) {
          setEnterprise(initEnterprise);
        } else {
          setEnterprise(null);
        }
        setOffer({
          expirationDate: toStartOfDayISO(addDays(new Date(), 20), timeZone),
        });
      }
    }
  }, [open, detailOffer, initEnterprise]);

  useEffect(() => {
    if (isDate(offer.startDate) && isValid(offer.startDate)) {
      const startDate = offer.startDate;
      setOffer((offer) => ({
        ...offer,
        expirationDate: toStartOfDayISO(addDays(new Date(startDate), 20), timeZone),
      }));
    }
  }, [offer.startDate]);

  useEffect(() => {
    if (followUpDate && initialFollowUpDate.current === null) {
      initialFollowUpDate.current = followUpDate.getTime();
    }
  }, [followUpDate]);

  useEffect(() => {
    if (offerUrl !== undefined && !offer?.offerId) {
      if (offerUrl.data) {
        setOffer(offerUrl.data);
        if (offerUrl.data.enterprise) {
          setEnterprise(offerUrl.data.enterprise);
        }
        if (offerUrl.data.contact) {
          setContact(offerUrl.data.contact);
        }
      } else {
        setErrors((errors) => ({ ...errors, offerUrl: false }));
      }
      setSearchUrlDone(true);
    }
  }, [offerUrl]);

  const handleUpdateOffer = async (
    enterprise: IEnterpriseBody,
    enterpriseToPush: IOfferPayloadEnterprise,
    contact: Partial<IContact> | null
  ) => {
    // if candidate is for jobboard favorite
    if (offer.offerId && offer.source && isInternalOfferSource(offer.source) && !candidateId && appellation) {
      const offerToUpdate = formatPutOffer(offer as IOffer, appellation, user._id, timeZone);

      //put offer
      const result = await putOffer(offer.offerId, {
        offer: offerToUpdate,
        enterprise: enterpriseToPush,
        contact: contact ?? undefined,
      });
      if (result.status === 204) {
        window.publish("feedback", {
          severity: "success",
          message: "L'offre a bien été enregistrée",
        });
        navigate(`/v2/offer/${offer.offerId}?tab=suggest-candidate`);
      }
      if (isOnConfirmOffer(onConfirm, true)) {
        onConfirm({ ...offer, contact: contact ?? undefined, enterprise } as IOffer);
      }
    } else if (searchUrlDone && offer.offerId && isInternalOfferSource(offer.source) && candidateId && appellation) {
      const offerToUpdate = formatPutOffer(offer as IOffer, appellation, user._id, timeZone);

      const result = await putOffer(offer.offerId, {
        offer: offerToUpdate,
        enterprise: enterpriseToPush,
        contact: contact ?? undefined,
      });
      if (result.status === 204) {
        if (!offer.metadata?.starredAt) {
          const result = await putOfferPinned(candidateId, offer.offerId);
          if (result.status === 201) {
            window.publish("feedback", {
              severity: "success",
              message: "L'offre a bien été enregistrée et ajoutée aux offres épinglées",
            });
          }
        } else {
          window.publish("feedback", {
            severity: "success",
            message: "L'offre a bien été enregistrée",
          });
        }
      }
      if (isOnConfirmOffer(onConfirm, true)) {
        onConfirm({ ...offer, contact: contact ?? undefined, enterprise } as IOffer);
      }
    }
  };

  const handleCreateOffer = async (enterprise: IOfferPayloadEnterprise, contact: Partial<IContact> | null) => {
    if (offer.offerId && candidateId) {
      const result = await putOfferPinned(candidateId, offer.offerId);
      if (result.status === 201) {
        window.publish("feedback", {
          severity: "success",
          message: "L'offre a bien été ajoutée aux offres épinglées",
        });
        if (isOnConfirmOffer(onConfirm, true)) {
          onConfirm(offer as IOffer);
        }
      }
      return;
    }
    if (appellation !== null) {
      const offerToCreate = formatCreateOffer(offer as IOffer, appellation);
      const result = await postOffer({
        offer: offerToCreate,
        enterprise,
        createOffer,
        contact: contact ?? undefined,
        starCandidate: candidateId,
      });

      if (result.status === 201) {
        window.publish("feedback", {
          severity: "success",
          message: "L'offre a bien été créée",
        });
        if (isOnConfirmOffer(onConfirm, true)) {
          onConfirm({ ...result?.data });
        }
      }
    }
  };

  const handleCreateApply = async (
    enterprise: IOfferPayloadEnterprise | undefined,
    contact: Partial<IContact> | null
  ): Promise<boolean> => {
    if (!apply.status) {
      setErrors((errors) => ({ ...errors, status: true }));
      window.publish("feedback", {
        severity: "error",
        message: "Le statut est obligatoire pour créer une candidature !",
      });
      return false;
    }
    if (offer.offerId && candidateId && managementProcess) {
      const newApply: IPostApplyOffer = {
        source: "manual",
        candidate: candidateId,
        managementProcess: managementProcess,
        kind: "job",
        status: apply.status,
        offer: offer as unknown as IOfferPostApply,
        contractStartDate: apply.contractStartDate,
        contractEndDate: getApplyContractEndDate(apply.contractStartDate, offer.contractType, offer.contractDuration),
      };

      const result = await postApply(newApply);
      if (result.status === 201) {
        window.publish("feedback", {
          severity: "success",
          message: "La candidature a bien été créée",
        });
        if (isOnConfirmApply(onConfirm, true)) {
          onConfirm({ ...result?.data.apply });
        }
        return true;
      }
      return false;
    }
    // biome-ignore lint/style/noNonNullAssertion: <explanation>
    const offerToCreate = formatCreateOffer(offer as IOffer, appellation!);

    if (candidateId && managementProcess) {
      const contractEndDate: Date | undefined = undefined;
      const newApply: IPostApplyAndOffer = {
        source: "manual",
        candidate: candidateId,
        managementProcess: managementProcess,
        contractStartDate: apply.contractStartDate,
        contractEndDate: getApplyContractEndDate(apply.contractStartDate, offer.contractType, offer.contractDuration),
        kind: "job",
        status: apply.status,
        offer: {
          offer: offerToCreate,
          createOffer,
          enterprise,
          contact: contact ?? undefined,
        },
      };

      if (apply.isFollowUpGenerated) {
        newApply.isFollowUpGenerated = true;
        newApply.contractStartDate = apply.contractStartDate;
      }

      if (apply.itwDate) {
        newApply.itwDate = apply.itwDate;
      }

      const result = await postApplyAndOffer(newApply);

      if (result.status === 201) {
        if (result.data.offer.offerId) {
          await sendFeedback({
            candidate: candidateId,
            event: "proposed",
            kind: result.data.kind,
            recommendations: [result.data.offer.offerId],
            filtersHash: result.data.offer.metadata?.filtersHash,
          });
        }
        window.publish("feedback", {
          severity: "success",
          message: "L'offre et la candidature ont bien été créées",
        });
        if (result.data.isFollowUpGenerated && followUpDate) {
          const action = generateJobFollowUpAction("Suivi post-embauche", followUpDate, result.data, user);
          const actionResult = await createAction([action]);
          if (actionResult.status === 200) {
            window.publish("feedback", {
              severity: "success",
              message: "L'action a bien été créée",
            });
          }
        }
        if (isOnConfirmApply(onConfirm, true)) {
          onConfirm({ ...result?.data });
        }
        return true;
      }
      return false;
    }
    return false;
  };

  const handleEditApply = async (applyId: ObjectId, contact: IContact | null): Promise<boolean> => {
    const updatedOffer = {
      ...offer,
      enterprise: enterprise ? formatEnterprise(enterprise, isSharedEnterprise) : undefined,
      contact: contact ? formatContact(contact) : null,
    };
    try {
      const contractEndDate = getApplyContractEndDate(
        apply.contractStartDate,
        offer.contractType,
        offer.contractDuration
      );
      const patchPayload: IApplyPatch = {
        offer: updatedOffer,
        ...(apply.status && { status: apply.status }),
        ...(apply.itwDate && { itwDate: apply.itwDate }),
        ...(apply.contractStartDate && { contractStartDate: apply.contractStartDate }),
        ...(apply.isFollowUpGenerated && { isFollowUpGenerated: true }),
        ...(contractEndDate && { contractEndDate }),
        ...(!initialCreateOffer.current && createOffer && { createOffer: true }),
      };
      const result = await patchApply(applyId, patchPayload);
      if (result.status === 204 || result.status === 200) {
        if (result.data?.offerId) {
          updatedOffer.offerId = result.data.offerId;
        }
        window.publish("feedback", {
          severity: "success",
          message: "La candidature a bien été mise à jour",
        });
        const newlyEnabled = !initialIsFollowUpGenerated.current && apply.isFollowUpGenerated;
        const dateChanged =
          initialIsFollowUpGenerated.current &&
          apply.isFollowUpGenerated &&
          followUpDate !== null &&
          initialFollowUpDate.current !== null &&
          followUpDate.getTime() !== initialFollowUpDate.current;
        if ((newlyEnabled || dateChanged) && followUpDate && candidateId) {
          const action = generateJobFollowUpAction(
            "Suivi post-embauche",
            followUpDate,
            { _id: applyId, offer: updatedOffer, candidate: { _id: candidateId } } as IApply,
            user
          );
          const actionResult = await createAction([action]);
          if (actionResult.status === 200) {
            window.publish("feedback", {
              severity: "success",
              message: "L'action a bien été créée",
            });
          }
        }
        if (isOnConfirmApply(onConfirm, true)) {
          onConfirm({
            ...({} as IApply),
            ...patchPayload,
            _id: applyId,
            offer: {
              ...updatedOffer,
              enterprise: enterprise ?? undefined,
              contact: contact ?? undefined,
            } as IApply["offer"],
          });
        }
        return true;
      }
      return false;
    } catch {
      window.publish("feedback", {
        severity: "error",
        message: "Une erreur est survenue lors de la mise à jour de la candidature.",
      });
      return false;
    }
  };

  const onSubmit = async () => {
    if (searchUrlDone && candidateId && !isApply && offer.offerId && !isInternalOfferSource(offer.source)) {
      const result = await putOfferPinned(candidateId, offer.offerId);
      if (result.status === 201) {
        window.publish("feedback", {
          severity: "success",
          message: "L'offre a bien été ajoutée aux offres épinglées",
        });
        if (isOnConfirmOffer(onConfirm, true)) {
          onConfirm(offer as IOffer);
        }
      }
      resetModal();
      return false;
    }
    if (searchUrlDone && !candidateId && !isApply && offer.offerId && !isInternalOfferSource(offer.source)) {
      navigate(`/v2/offer/${offer.offerId}?tab=suggest-candidate`);
      if (isOnConfirmOffer(onConfirm, true)) {
        onConfirm(offer as IOffer);
      }
      resetModal();
      return false;
    }
    const { hasError, formErrors, message } = verifyOfferFields(
      offer,
      enterprise,
      appellation,
      apply,
      errors,
      applyFollowUp,
      createOffer
    );
    setErrors(formErrors);
    if (hasError) {
      window.publish("feedback", {
        severity: "error",
        message,
      });
      return false;
    }
    if (mode === "editApply" && applyId) {
      const success = await handleEditApply(applyId, contact);
      if (!success) {
        return false;
      }
      resetModal();
      return true;
    }
    if (searchUrlDone && candidateId && !isApply && isInternalOfferSource(offer.source) && enterprise) {
      const enterpriseToPush = formatEnterprise(enterprise, isSharedEnterprise);
      await handleUpdateOffer(enterprise, enterpriseToPush, formatContact(contact));
    } else if (searchUrlDone && !candidateId && !isApply && isInternalOfferSource(offer.source) && enterprise) {
      const enterpriseToPush = formatEnterprise(enterprise, isSharedEnterprise);
      await handleUpdateOffer(enterprise, enterpriseToPush, formatContact(contact));
    } else if (isApply) {
      const enterpriseToPush = enterprise ? formatEnterprise(enterprise, isSharedEnterprise) : undefined;
      const success = await handleCreateApply(enterpriseToPush, formatContact(contact));
      if (!success) {
        return false;
      }
    } else if (offer.offerId && enterprise && candidateId) {
      const enterpriseToPush = formatEnterprise(enterprise, isSharedEnterprise);
      await handleCreateOffer(enterpriseToPush, formatContact(contact));
      setContact(null);
    } else if (offer.offerId && enterprise) {
      const enterpriseToPush = formatEnterprise(enterprise, isSharedEnterprise);
      await handleUpdateOffer(enterprise, enterpriseToPush, formatContact(contact));
    } else if (!hasError && enterprise) {
      const enterpriseToPush = formatEnterprise(enterprise, isSharedEnterprise);
      await handleCreateOffer(enterpriseToPush, formatContact(contact));
      setContact(null);
    }

    resetModal();
    return true;
  };

  const resetModal = () => {
    setOffer({});
    setApply(initApply);
    setContact(null);
    setEnterprise(null);
    setAppellation(null);
  };

  const onCancelModal = () => {
    onCancel();
    resetModal();
  };

  return {
    onSubmit,
    onCancel: onCancelModal,
    offer,
    setOffer,
    createOffer,
    setCreateOffer,
    enterprise,
    setEnterprise,
    isSharedEnterprise,
    setIsSharedEnterprise,
    contact,
    setContact,
    setErrors,
    searchUrlDone,
    setSearchUrlDone,
    getOfferUrl,
    loadingUrl,
    cancelGetOfferUrl,
    errors,
    disabled:
      loadingPost ||
      loadingPut ||
      loadingPostApplyAndOffer ||
      loadingPostApply ||
      loadingPatchApply ||
      loadingUrl ||
      loadingPutPinned ||
      loadingCreateAction ||
      Boolean(searchUrlDone && offer?.offerId && !isInternalOfferSource(offer.source)),
    loading:
      loadingPut ||
      loadingPost ||
      loadingPostApplyAndOffer ||
      loadingPostApply ||
      loadingPatchApply ||
      loadingPutPinned,
    apply,
    setApply,
    followUpDate,
    setFollowUpDate,
    appellation,
    setAppellation,
    applyFollowUp,
    mode,
  };
};
